import React from 'react';
import { getSelectedOption } from '../../pages/maturity-assesment/maturityOnlyResults/utils';

type DetailedResultsProps = {
  show: boolean;
  onShowChange: (show: boolean) => void;
  assessmentData: any;
  answers: number[];
};

export default function DetailedResults({ show, onShowChange, assessmentData, answers }: DetailedResultsProps) {
  return (
    <section className={`mb-8 ${show ? 'bg-slate-800 rounded-xl shadow-md p-4 sm:p-6 border border-slate-700' : 'bg-slate-800 px-4 py-3 rounded-lg shadow-sm border border-slate-700 flex items-center justify-between'}`}>
      {!show ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
          <div>
            <div className="text-white text-sm font-semibold">Detailed Maturity Responses</div>
            <div className="text-slate-400 text-xs">View individual question responses</div>
          </div>
          <button 
            className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium px-3 py-1.5 rounded-md border border-slate-600 transition-colors w-full sm:w-auto"
            onClick={() => onShowChange(true)}
          >
            Show detailed maturity responses
          </button>
        </div>
      ) : (
        <>
          <div className="text-white text-lg font-semibold mb-4">Detailed Maturity Responses</div>
          <div className="text-slate-400 text-sm mb-6">View individual question responses and selected assessment choices</div>
          
          <div className="flex justify-center sm:justify-end mb-6">
            <button 
              className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium px-3 py-1.5 rounded-md border border-slate-600 transition-colors w-full sm:w-auto"
              onClick={() => onShowChange(false)}
            >
              Hide detailed maturity responses
            </button>
          </div>
        </>
      )}

      {show && assessmentData && (
        <div className="space-y-6">
          {/* Operational Excellence Questions */}
          <div>
            <div className="text-primary text-lg font-semibold mb-4">Operational Excellence</div>
            <div className="space-y-3">
              {[
                { question: 'Account reconciliation', questionIndex: 0, answer: answers[0] || 0 },
                { question: 'Journal entries', questionIndex: 1, answer: answers[1] || 0 },
                { question: 'Accruals and provisions', questionIndex: 2, answer: answers[2] || 0 },
                { question: 'Intercompany transactions', questionIndex: 3, answer: answers[3] || 0 },
                { question: 'Close checklist and task management', questionIndex: 4, answer: answers[4] || 0 },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-slate-700 rounded-lg p-3 sm:px-4 sm:py-3 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between border border-slate-600 hover:bg-slate-600 transition-colors gap-2 sm:gap-0"
                >
                  <div>
                    <div className="text-white text-sm font-medium">{item.question}</div>
                    <div className="text-slate-400 text-xs italic mt-1">{getSelectedOption(item.questionIndex, item.answer)}</div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-slate-300 text-sm font-semibold">{item.answer} / 5</div>
                    <div className="w-24 h-1.5 bg-slate-600 mt-1 rounded-full overflow-hidden">
                      <div className="bg-primary h-full rounded-full" style={{ width: `${(item.answer / 5) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strategic Maturity Questions */}
          <div>
            <div className="text-primary text-lg font-semibold mb-4">Strategic Maturity</div>
            <div className="space-y-3">
              {[
                { question: 'Exception handling and escalation', questionIndex: 5, answer: answers[5] || 0 },
                { question: 'Automation coverage across R2R processes', questionIndex: 6, answer: answers[6] || 0 },
                { question: 'Process standardization and documentation', questionIndex: 7, answer: answers[7] || 0 },
                { question: 'Technology stack integration', questionIndex: 8, answer: answers[8] || 0 },
                { question: 'Data integrity and validation', questionIndex: 9, answer: answers[9] || 0 },
                { question: 'Metrics and continuous improvement', questionIndex: 10, answer: answers[10] || 0 },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-slate-700 rounded-lg p-3 sm:px-4 sm:py-3 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between border border-slate-600 hover:bg-slate-600 transition-colors gap-2 sm:gap-0"
                >
                  <div>
                    <div className="text-white text-sm font-medium">{item.question}</div>
                    <div className="text-slate-400 text-xs italic mt-1">{getSelectedOption(item.questionIndex, item.answer)}</div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-slate-300 text-sm font-semibold">{item.answer} / 5</div>
                    <div className="w-24 h-1.5 bg-slate-600 mt-1 rounded-full overflow-hidden">
                      <div className="bg-primary h-full rounded-full" style={{ width: `${(item.answer / 5) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

